/************************************************************************************

* [SECTION]: COLLECTION FILTER

*************************************************************************************/
if (!customElements.get('l-collection-filters')) {
	class LTACollectionFilters extends HTMLElement {
		constructor() {
			super()

			this.facetFormEl = null
			this.footerControlsEl = null
			this.sectionId = null
			this.hasSortBy = false
			this.data = {
				searchParamsInitial: '',
				searchParamsPrev: '',
				filterData: [],
			}
			this.abortController = null
			this.filterDrawer = null

			this._onFormSubmit = this._onFormSubmit.bind(this)
			this._onFormInput = l_debounce(this._onFormInput.bind(this), 800)
			this._onTriggeredFilter = this._onTriggeredFilter.bind(this)
			this._onPopState = this._onPopState.bind(this)
		}

		connectedCallback() {
			this.facetFormEl = this.querySelector('form')
			this.footerControlsEl = document.getElementById(
				`CollectionFiltersControls-${this.dataset.type}-${this.dataset.deviceVersion}`
			)

			this.sectionId = this.dataset.sectionId
			this.hasSortBy = !!this.facetFormEl.querySelector('select[name="sort_by"]')
			const searchSlice = window.location.search.slice(1)
			this.data = {
				searchParamsInitial: searchSlice,
				searchParamsPrev: searchSlice,
				filterData: [],
			}
			this.abortController = new AbortController()

			this._setupEvents()
		}

		disconnectedCallback() {
			this._removeEvents()
		}

		_setupEvents() {
			// Form submit (footer controls)
			if (this.footerControlsEl && this.facetFormEl) {
				this.facetFormEl.addEventListener('submit', this._onFormSubmit)
			}

			// Input event
			if (this.facetFormEl) {
				this.facetFormEl.addEventListener('input', this._onFormInput)
			}

			// Listen for triggered filter
			document.addEventListener('l:filters:triggeredFilter', this._onTriggeredFilter)

			// History popstate
			window.addEventListener('popstate', this._onPopState)

			// Filter-count updates
			this._updateFilterCountEventDelegation()
		}

		_removeEvents() {
			if (this.facetFormEl) {
				this.facetFormEl.removeEventListener('submit', this._onFormSubmit)
				this.facetFormEl.removeEventListener('input', this._onFormInput)
			}
			document.removeEventListener('l:filters:triggeredFilter', this._onTriggeredFilter)
			window.removeEventListener('popstate', this._onPopState)
		}

		_onFormSubmit(event) {
			event.preventDefault()

			const newSearchParams = this._createSearchParams(this.facetFormEl)
			this._handleFiltersChange(newSearchParams)
		}

		_onFormInput(event) {
			event.preventDefault()

			const newSearchParams = this._createSearchParams(this.facetFormEl)

			this.dispatchEvent(
				new CustomEvent('l:filters:input', {
					bubbles: true,
					detail: { searchParamsPrev: this.data.searchParamsPrev, newSearchParams },
				})
			)

			if (!this.footerControlsEl) this._handleFiltersChange(newSearchParams)
		}

		_onTriggeredFilter(event) {
			const newSearchParams = event.detail?.newSearchParams
			if (typeof newSearchParams === 'string') {
				this._handleFiltersChange(newSearchParams)
			}
		}

		_onPopState(event) {
			const searchParams = event.state ? event.state.searchParams : this.data.searchParamsInitial
			if (searchParams === this.data.searchParamsPrev) return
			this._handleFiltersChange(searchParams, false)
		}

		_handleFiltersChange(newSearchParams, updateURLHash = true) {
			if (this.data.searchParamsPrev === newSearchParams) return

			this.data.searchParamsPrev = newSearchParams
			this.dispatchEvent(new CustomEvent('l:filters:loading', { bubbles: true }))

			const newSearchUrl = `${window.location.pathname}?section_id=${this.sectionId}&${newSearchParams}`
			const filterDataMatch = (el) => el.url === newSearchUrl

			if (this.data.filterData.some(filterDataMatch)) {
				this._renderSectionFromCache(filterDataMatch)
			} else {
				this._renderSectionFromFetch(newSearchUrl)
			}

			if (updateURLHash) this._updateURLHash(newSearchParams)

			if (!this.filterDrawer) {
				this.filterDrawer = this.closest('l-drawer')
			}

			if (this.filterDrawer && typeof this.filterDrawer.drawer_close === 'function') {
				this.filterDrawer.drawer_close()
			}
		}

		_renderSectionFromCache(filterDataMatch) {
			const cached = this.data.filterData.find(filterDataMatch)
			if (cached && cached.html) {
				this._renderContent(cached.html)
			}
		}

		_renderSectionFromFetch(url) {
			this.abortController?.abort()
			this.abortController = new AbortController()

			fetch(url, { signal: this.abortController.signal })
				.then((resp) => resp.text())
				.then((html) => {
					this.data.filterData = [...this.data.filterData, { html, url }]
					this._renderContent(html)
				})
				.catch((e) => {
					if (e.name !== 'AbortError') {
						console.error('[LTACollectionFilters] Fetch error:', e)
					}
				})
		}

		_renderContent(html) {
			if (!html) return
			const newHTML = l_getSectionDOM(html)

			console.log(newHTML)

			// Update l-collapsible-tab content/triggers based on ids
			this.querySelectorAll('l-collapsible-tab').forEach((element) => {
				const id = element.id
				if (!id) return
				const newElement = newHTML.querySelector(`#${id}`)
				if (!newElement) return

				const triggerEl = newElement.querySelector('[data-trigger]')
				const contentEl = newElement.querySelector('[data-content]')
				const oldTrigger = element.querySelector('[data-trigger]')
				const oldContent = element.querySelector('[data-content]')

				if (triggerEl && oldTrigger) oldTrigger.innerHTML = triggerEl.innerHTML
				if (contentEl && oldContent) oldContent.innerHTML = contentEl.innerHTML
			})

			// Update footer controls
			if (this.footerControlsEl) {
				const newFooterControlsEl = newHTML.querySelector(`#${this.footerControlsEl.id}`)
				if (newFooterControlsEl) {
					this.footerControlsEl.innerHTML = newFooterControlsEl.innerHTML
				}
			}

			// Re-init trigger/input listeners and filter count
			this._removeEvents()
			this._setupEvents()

			this.dispatchEvent(
				new CustomEvent('l:filters:rendered', {
					bubbles: true,
					detail: { html: newHTML },
				})
			)
		}

		_updateURLHash(newSearchParams) {
			const url = `${window.location.pathname}${newSearchParams ? '?' + newSearchParams : ''}`
			history.pushState({ searchParams: newSearchParams }, '', url)
		}

		_createSearchParams(form) {
			const formData = new FormData(form)
			;['filter.v.price.lte', 'filter.v.price.gte'].forEach((key) => {
				if (formData.get(key) === '') formData.delete(key)
			})
			return new URLSearchParams(formData).toString()
		}

		_updateFilterCountEventDelegation() {
			this.querySelectorAll('l-collapsible-tab').forEach((tabEl) => {
				tabEl.addEventListener('input', () => {
					const selectedSortbyEl = tabEl.querySelector('input:checked')
					const sortbyEl = tabEl.querySelector('[data-selected-sort-by]')
					if (sortbyEl && selectedSortbyEl) {
						sortbyEl.textContent = selectedSortbyEl.dataset.optionName
					}
					const count = tabEl.querySelectorAll('input:checked').length
					const countEl = tabEl.querySelector('[data-filter-count]')
					if (countEl) {
						countEl.textContent = `+${count}`
						countEl.classList.toggle('l-hidden', count === 0)
					}
				})
			})
		}
	}

	customElements.define('l-collection-filters', LTACollectionFilters)
}

/************************************************************************************

* [COMPONENTS]: FILTER COMPONENTS

*************************************************************************************/

if (!customElements.get('l-price-range')) {
	class LTAPriceRange extends HTMLElement {
		constructor() {
			super()

			this.rangeMin = null
			this.rangeMax = null
			this.inputMin = null
			this.inputMax = null
			this._handleInputFocus = this._handleInputFocus.bind(this)
			this._handleInputChange = this._handleInputChange.bind(this)
			this._handleRangeChange = this._handleRangeChange.bind(this)
			this._handleRangeInput = this._handleRangeInput.bind(this)
		}

		connectedCallback() {
			this.parentFormEl = this.closest('form')
			this.rangeMin = this.querySelector('input[type="range"]:first-child')
			this.rangeMax = this.querySelector('input[type="range"]:last-child')
			this.inputMin = this.querySelector('input[name="filter.v.price.gte"]')
			this.inputMax = this.querySelector('input[name="filter.v.price.lte"]')
			this._bindEvents()
		}

		_bindEvents() {
			if (!this.inputMin || !this.inputMax || !this.rangeMin || !this.rangeMax) return

			this.inputMin.addEventListener('focus', this._handleInputFocus)
			this.inputMax.addEventListener('focus', this._handleInputFocus)

			this.inputMin.addEventListener('change', (e) => this._handleInputChange(e, 'min'))
			this.inputMax.addEventListener('change', (e) => this._handleInputChange(e, 'max'))

			this.rangeMin.addEventListener('change', (e) => this._handleRangeChange(e, 'min'))
			this.rangeMax.addEventListener('change', (e) => this._handleRangeChange(e, 'max'))

			this.rangeMin.addEventListener('input', (e) => this._handleRangeInput(e, 'min'))
			this.rangeMax.addEventListener('input', (e) => this._handleRangeInput(e, 'max'))
		}

		_handleInputFocus(event) {
			event.target.select()
		}

		_handleInputChange(event, type) {
			event.preventDefault()
			const isMin = type === 'min'
			const input = isMin ? this.inputMin : this.inputMax
			const pairInput = isMin ? this.inputMax : this.inputMin
			const range = isMin ? this.rangeMin : this.rangeMax
			const cssVar = isMin ? '--from' : '--to'
			const modifier = isMin ? -1 : 1

			let targetVal = parseInt(event.target.value, 10) || 0
			let limitVal = parseInt(pairInput.value, 10)

			if (isNaN(limitVal)) {
				limitVal = parseInt(event.target[isMin ? 'max' : 'min'], 10) || 0
			}

			let boundVal
			if (isMin) {
				boundVal = Math.max(Math.min(targetVal, limitVal + modifier * -1), parseInt(event.target.min, 10))
			} else {
				boundVal = Math.min(Math.max(targetVal, limitVal + modifier), parseInt(event.target.max, 10))
			}

			event.target.value = boundVal
			range.value = boundVal
			this._setRangeCss(range, cssVar, boundVal)
		}

		_handleRangeChange(event, type) {
			event.stopPropagation()
			const input = type === 'min' ? this.inputMin : this.inputMax
			input.value = event.target.value
			input.dispatchEvent(new Event('change', { bubbles: true }))
		}

		_handleRangeInput(event, type) {
			const isMin = type === 'min'
			const input = isMin ? this.inputMin : this.inputMax
			const pairInput = isMin ? this.inputMax : this.inputMin
			const cssVar = isMin ? '--from' : '--to'

			const pairValue = parseInt(pairInput.value, 10)
			let value = parseInt(event.target.value, 10)

			let limit
			if (isNaN(pairValue)) {
				limit = parseInt(event.target[isMin ? 'max' : 'min'], 10)
			} else {
				limit = pairValue
			}

			value = isMin ? Math.min(value, limit - 1) : Math.max(value, limit + 1)

			event.target.value = value
			this._setRangeCss(event.target, cssVar, value)
			input.value = value
		}

		_setRangeCss(rangeElement, cssVar, value) {
			const max = parseInt(rangeElement.max, 10) || 1
			rangeElement.parentElement.style.setProperty(cssVar, `${(value / max) * 100}%`)
		}

		_updateAllVisuals() {
			const minVal = parseInt(this.inputMin.min, 10) || 0
			const maxVal = parseInt(this.inputMax.max, 10) || 0

			this.rangeMin.value = minVal
			this.rangeMax.value = maxVal

			this._setRangeCss(this.rangeMin, '--from', minVal)
			this._setRangeCss(this.rangeMax, '--to', maxVal)
		}
	}

	customElements.define('l-price-range', LTAPriceRange)
}

if (!customElements.get('l-collection-count')) {
	class LTACollectionCount extends HTMLElement {
		constructor() {
			super()

			this._onFiltersLoading = this._onFiltersLoading.bind(this)
			this._onFiltersRendered = this._onFiltersRendered.bind(this)
		}

		connectedCallback() {
			document.addEventListener('l:filters:loading', this._onFiltersLoading)
			document.addEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:loading', this._onFiltersLoading)
			document.removeEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		_onFiltersLoading() {
			this.classList.add('is-loading')
		}

		_onFiltersRendered(event) {
			this.classList.remove('is-loading')

			const newHTML = event.detail.html
			const newCollectionCount = newHTML.querySelector('l-collection-count')

			if (newCollectionCount) {
				this.innerHTML = newCollectionCount.innerHTML
			}
		}
	}

	customElements.define('l-collection-count', LTACollectionCount)
}

if (!customElements.get('l-filter-footer-controls')) {
	class LTAFilterFooterControls extends HTMLElement {
		constructor() {
			super()

			this._onFiltersInput = this._onFiltersInput.bind(this)
			this._onFiltersLoading = this._onFiltersLoading.bind(this)
			this._onFiltersRendered = this._onFiltersRendered.bind(this)
			this._onClick = this._onClick.bind(this)
		}

		connectedCallback() {
			this._cacheElements()

			document.addEventListener('l:filters:input', this._onFiltersInput)
			document.addEventListener('l:filters:loading', this._onFiltersLoading)
			document.addEventListener('l:filters:rendered', this._onFiltersRendered)

			this.addEventListener('click', this._onClick)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:input', this._onFiltersInput)
			document.removeEventListener('l:filters:loading', this._onFiltersLoading)
			document.removeEventListener('l:filters:rendered', this._onFiltersRendered)

			this.removeEventListener('click', this._onClick)
		}

		_cacheElements() {
			this.resetBtnEl = this.querySelector('a')
			this.submitBtnEl = this.querySelector('button[type="submit"]')
		}

		_onFiltersInput(event) {
			const { searchParamsPrev, newSearchParams } = event.detail
			const isUnchanged = searchParamsPrev == newSearchParams

			if (this.submitBtnEl) {
				this.submitBtnEl.classList.toggle('is-disabled', isUnchanged)
				this.submitBtnEl.disabled = isUnchanged
			}

			if (this.resetBtnEl) {
				this.resetBtnEl.classList.toggle('is-disabled', isUnchanged)
				this.resetBtnEl.disabled = isUnchanged
			}
		}

		_onFiltersLoading() {
			if (this.submitBtnEl) {
				this.submitBtnEl.classList.add('is-loading')
			}
		}

		_onFiltersRendered(event) {
			if (this.submitBtnEl) {
				this.submitBtnEl.classList.remove('is-loading')
			}

			this._cacheElements()
		}

		_onClick(event) {
			if (this.resetBtnEl && (event.target === this.resetBtnEl || this.resetBtnEl.contains(event.target))) {
				event.preventDefault()

				this._clearFiltersWithoutAjax()

				this.dispatchEvent(
					new CustomEvent('l:filters:triggeredFilter', {
						bubbles: true,
						detail: { newSearchParams: '' },
					})
				)
			}
		}

		_clearFiltersWithoutAjax() {
			const filterDrawerEl = this.closest('#collection-filters-drawer')
			if (!filterDrawerEl) return

			const filterFormEl = filterDrawerEl.querySelector('#CollectionFiltersForm-drawer-all')
			if (!filterFormEl) return

			// Reset form
			filterFormEl.reset()

			// Update price range visuals
			const priceRangeEls = filterFormEl.querySelectorAll('l-price-range')
			priceRangeEls.forEach((priceRangeEl) => {
				priceRangeEl._updateAllVisuals()
			})
		}
	}

	customElements.define('l-filter-footer-controls', LTAFilterFooterControls)
}

/************************************************************************************

* [COMPONENT]: PRODUCT GRID

*************************************************************************************/

if (!customElements.get('l-collection-grid')) {
	class LTACollectionGrid extends HTMLElement {
		constructor() {
			super()

			this.onFilterLoading = this.onFilterLoading.bind(this)
			this.onFilterRendered = this.onFilterRendered.bind(this)
		}

		connectedCallback() {
			document.addEventListener('l:filters:loading', this.onFilterLoading)
			document.addEventListener('l:filters:rendered', this.onFilterRendered)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:loading', this.onFilterLoading)
			document.removeEventListener('l:filters:rendered', this.onFilterRendered)
		}

		onFilterLoading() {
			this.classList.add('is-loading')
		}

		onFilterRendered(event) {
			const newHTML = event.detail.html
			if (!newHTML) return

			const newProductGridEl = newHTML.querySelector('[data-product-grid]')
			if (newProductGridEl) {
				this.innerHTML = newProductGridEl.innerHTML
			}

			this.classList.remove('is-loading')
		}
	}

	customElements.define('l-collection-grid', LTACollectionGrid)
}

